/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import type {
  PcgActResult,
  PcgBeatAwareResult,
  PcgVendorEvidence,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"
import type { PluginId } from "./plugin-definitions"

/**
 * Plugins-rack Result rows built from the ACT and BeatAware results the
 * heartbeat service stored for a recording. The row labels are the handoff
 * rack's own (`pluginDefinitions[...].result`); only the values come from the
 * vendor data.
 *
 * `available` is false when there is no ready result to review (never
 * processed, still processing, failed, errored, or unreadable): the rack then
 * shows the bare LED ring and disables the review buttons, so result
 * availability is never mistaken for a clinical disposition (third-party
 * evidence spec §6.4).
 */
interface PluginResult {
  available: boolean
  rows: [string, string][]
}

const NO_RESULT: PluginResult = { available: false, rows: [["Status", "No result for this recording"]] }

const unavailable = (status: string): PluginResult => ({ available: false, rows: [["Status", status]] })

// ACT diagnosis codes as written by the heartbeat service.
const ACT_ERROR = -1
const ACT_INSUFFICIENT_DATA = 0
const ACT_NORMAL = 1
const ACT_ABNORMAL = 2

const actResult = (act: PcgActResult | null | undefined): PluginResult => {
  if (!act) {
    return NO_RESULT
  }
  switch (act.diagnosis) {
    case ACT_INSUFFICIENT_DATA:
      return {
        available: true,
        rows: [
          ["Recording quality", "Insufficient"],
          ["Vendor output", "Unable to assess"],
          ["Medaica mapping", "Not usable as supporting evidence"],
        ],
      }
    case ACT_NORMAL:
    case ACT_ABNORMAL:
      return {
        available: true,
        rows: [
          ["Recording quality", "Sufficient"],
          ["Vendor output", act.diagnosis === ACT_NORMAL ? "Normal" : "Abnormal"],
          ["Medaica mapping", "Structural Sound supporting evidence"],
        ],
      }
    case ACT_ERROR:
      return unavailable("Vendor processing error")
    default:
      return unavailable("Unrecognized vendor result")
  }
}

/** Leading number of a BeatAware cell: a plain number, or a "310 +/- 4" duration string. */
const cellNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === "string") {
    const match = /^\s*(\d+(?:\.\d+)?)/.exec(value)
    return match ? Number(match[1]) : null
  }
  return null
}

const median = (values: number[]): number | null => {
  if (!values.length) {
    return null
  }
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

const columnOf = (statistics: { [key: string]: unknown }[], key: string): number[] =>
  statistics.map((segment) => cellNumber(segment[key])).filter((value): value is number => value !== null)

const round = (value: number, digits = 0): string => String(Number(value.toFixed(digits)))

const DASH = "—"

const heartRateRow = (statistics: { [key: string]: unknown }[]): string => {
  const rates = columnOf(statistics, "Heart Rate (BPM)")
  const typical = median(rates)
  if (typical === null) {
    return DASH
  }
  const low = Math.min(...rates)
  const high = Math.max(...rates)
  return low === high ? `${round(typical)} bpm` : `${round(typical)} bpm (${round(low)}–${round(high)})`
}

const cycleTimingRow = (statistics: { [key: string]: unknown }[]): string => {
  const systole = median(columnOf(statistics, "Systole Duration (ms)"))
  const diastole = median(columnOf(statistics, "Diastole Duration (ms)"))
  const ratio = median(columnOf(statistics, "Systolic:Diastolic Duration Ratio"))
  const parts = [
    systole === null ? null : `Systole ${round(systole)} ms`,
    diastole === null ? null : `Diastole ${round(diastole)} ms`,
    ratio === null ? null : `S:D ${round(ratio, 2)}`,
  ].filter((part): part is string => part !== null)
  return parts.length ? parts.join(" · ") : DASH
}

const structuralSoundRow = (statistics: { [key: string]: unknown }[]): string => {
  const murmur = median(columnOf(statistics, "Chance of Murmur (%)"))
  const abnormality = median(columnOf(statistics, "Chance of Abnormality (%)"))
  const parts = [
    murmur === null ? null : `Murmur ${round(murmur)}%`,
    abnormality === null ? null : `Abnormality ${round(abnormality)}%`,
  ].filter((part): part is string => part !== null)
  return parts.length ? parts.join(" · ") : DASH
}

/**
 * BeatAware reports one row of statistics per analysed segment; each value
 * shown is the median across segments (heart rate also shows its range).
 */
const beatAwareResult = (beataware: PcgBeatAwareResult | null | undefined): PluginResult => {
  if (!beataware) {
    return NO_RESULT
  }
  switch (beataware.status) {
    case "processing":
      return unavailable("Processing")
    case "failed":
      return unavailable("Vendor could not process this recording")
    case "error":
      return unavailable("Vendor processing error")
    case "ready":
      if (!beataware.statistics?.length) {
        return unavailable("Result could not be read")
      }
      return {
        available: true,
        rows: [
          ["Heart Rate", heartRateRow(beataware.statistics)],
          ["Cycle Timing", cycleTimingRow(beataware.statistics)],
          ["Structural Sound", structuralSoundRow(beataware.statistics)],
          ["Field mapping", "Review individually before Panel inclusion"],
        ],
      }
    default:
      return unavailable("Unrecognized vendor status")
  }
}

const pluginResult = (id: PluginId, evidence: PcgVendorEvidence | null | undefined): PluginResult =>
  id === "act" ? actResult(evidence?.act) : beatAwareResult(evidence?.beataware)

export { actResult, beatAwareResult, pluginResult }
export type { PluginResult }
